import { Col, Row, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faSave } from '@fortawesome/free-solid-svg-icons';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { Container } from '../../../components/ui/Container/Container';
import ModalDialog from '../../../components/ui/ModalDialog/ModalDialog';
import useSystemCodeEditor from './useSystemCodeEditor';
import styles from './SystemCodeEditor.module.css';

interface ISystemCodeEditorProps {
  systemCodeId?: string;
}

export const SystemCodeEditor = ({ systemCodeId = '' }: ISystemCodeEditorProps) => {
  const manager = useSystemCodeEditor(systemCodeId);
  const ToastContainer = manager.ToastContainer;

  if (!systemCodeId) return <></>;

  return (
    <>
      <ToastContainer />
      <ModalDialog
        showDialog={manager.showUnsavedChangesDialog}
        heading="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard them?"
        okCallback={manager.discardUnsavedChanges}
        okButtonText="Yes, discard changes"
        cancelCallback={manager.closeUnsavedChangesDialog}
        cancelButtonText="No, keep editing"
      />
      <LoadingContainer isLoading={manager.loadingSystemCode}>
        <Container className={styles.editorContainer}>
          {/* Header Section */}
          <div className={styles.header}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                manager.back();
              }}
              className={styles.backLink}>
              <FontAwesomeIcon icon={faArrowLeftLong} className="me-2" />
              Back to Codes
            </a>
            <h2 className={styles.title}>{manager.systemCode.id === 0 ? 'Create' : 'Edit'} Code</h2>
          </div>

          {/* Main Form */}
          <Row className={styles.formRow}>
            <Col md={8} sm={12} className={styles.formColumn}>
              <div className={styles.card}>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label htmlFor="codeType" className={styles.label}>
                      Code Type
                    </label>
                    <input
                      type="text"
                      id="codeType"
                      className={`form-control ${
                        manager.fieldErrors.includes('overview:codeType') ? 'is-invalid' : ''
                      }`}
                      value={manager.systemCode.codeType}
                      onChange={(e) => manager.updateValue('codeType', e.target.value)}
                      placeholder="e.g., TYPE_CATEGORY"
                    />
                    {manager.fieldErrors.includes('overview:codeType') && (
                      <div className="invalid-feedback">Code Type is required.</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="codeValue" className={styles.label}>
                      Code Value
                    </label>
                    <input
                      type="text"
                      id="codeValue"
                      className={`form-control ${
                        manager.fieldErrors.includes('overview:codeValue') ? 'is-invalid' : ''
                      }`}
                      value={manager.systemCode.codeValue}
                      onChange={(e) => manager.updateValue('codeValue', e.target.value)}
                      placeholder="e.g., STANDARD"
                    />
                    {manager.fieldErrors.includes('overview:codeValue') && (
                      <div className="invalid-feedback">Code Value is required.</div>
                    )}
                  </div>
                </div>
                <div className="mb-2">
                  <label htmlFor="description" className={styles.label}>
                    Description
                  </label>
                  <textarea
                    id="description"
                    className="form-control"
                    value={manager.systemCode.description}
                    onChange={(e) => manager.updateValue('description', e.target.value)}
                    rows={3}
                    placeholder="Describe the system code"
                  />
                </div>
                <div className="mb-2">
                  <label htmlFor="info" className={styles.label}>
                    Info
                  </label>
                  <textarea
                    id="info"
                    className="form-control"
                    value={manager.systemCode.info}
                    onChange={(e) => manager.updateValue('info', e.target.value)}
                    rows={3}
                    placeholder="Info (ie. tooltips)"
                  />
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label htmlFor="sortOrder" className={styles.label}>
                      Sort Order
                    </label>
                    <input
                      type="number"
                      id="sortOrder"
                      min="0"
                      className="form-control"
                      value={manager.systemCode.sortOrder}
                      onChange={(e) =>
                        manager.updateValue('sortOrder', parseInt(e.target.value, 10))
                      }
                      placeholder="e.g., 1"
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-end h-100">
                      <Form.Check
                        type="checkbox"
                        id="active"
                        label="Active"
                        checked={manager.systemCode.active}
                        onChange={(e) => manager.updateValue('active', e.target.checked)}
                        className="mb-0"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Actions Card */}
              <div className={styles.actionCard}>
                <div className={styles.actionButtons}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={manager.saveChanges}
                    disabled={manager.isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={manager.cancelChanges}
                    disabled={manager.isSaving}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      manager.back();
                    }}
                    disabled={manager.isSaving}>
                    Back
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={manager.saveAndBack}
                    disabled={manager.isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save & Back
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={manager.saveAndNew}
                    disabled={manager.isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save & Add New
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </LoadingContainer>
    </>
  );
};
